'use client';

import { FC } from 'react';
import {
  LeaderboardDesktop,
  LeaderboardMobile,
} from '@wwe-portal/ui/features/leaderboard/leaderboard';
import {
  CollapsableContent,
  FactionLeaderboardRowDesktopLayout,
  LeaderboardRowDesktop,
  LeaderboardRowMobile,
} from '@wwe-portal/ui/features/leaderboard/leaderboard-row';
import { FactionAvatar } from '../common/image';

import { FactionLink } from '../common/link/faction-link';
import {
  CustomFactionColumnNames,
  FactionLeaderboardProps,
  FactionRankData,
} from './faction-leaderboard.type';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@wwe-portal/ui/components/arccordion';

const FactionNameAsTitle: FC<FactionRankData> = (item) => {
  return (
    <div className="flex items-center gap-3">
      {item.factionId && (
        <FactionAvatar
          factionId={item.factionId}
          factionName={item.faction}
          className="h-[26px] w-[26px] shrink-0"
        />
      )}
      <FactionLink
        className="text-text500 hover:text-text300 text-xs no-underline hover:no-underline"
        name={item.faction}
      >
        {item.name}
      </FactionLink>
    </div>
  );
};

const factionTableHeader = {
  rank: 'RANK',
  sub: null,
  main: {
    short: 'FACTION',
    long: 'FACTION',
  },
  point: 'POINTS',
};

export const FactionLeaderboardMobile = ({
  data,
  mobileHeader,
  customColumnNames,
}: Pick<
  FactionLeaderboardProps,
  'data' | 'mobileHeader' | 'customColumnNames'
>) => {
  return (
    <LeaderboardMobile
      header={mobileHeader}
      rows={
        <Accordion type="single" collapsible>
          {data.map((item, index) => (
            <AccordionItem
              value={index.toString()}
              key={index}
              data-rank={item.rank}
              className="odd:bg-back400 data-[rank='1']:border-back200 rounded-xl data-[rank='1']:border odd:md:bg-[#3d426e]"
            >
              <AccordionTrigger>
                <LeaderboardRowMobile
                  data={item}
                  title={<FactionNameAsTitle {...item} />}
                  subtitle={null}
                />
              </AccordionTrigger>
              <AccordionContent>
                <CollapsableContent
                  points={item.points}
                  expanded
                  endDate={item.endDate}
                  customColumnNames={customColumnNames}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      }
    />
  );
};

export const FactionRowHeaderDesktop: FC<{
  customColumn?: Partial<CustomFactionColumnNames>;
}> = ({ customColumn }) => {
  return (
    <FactionLeaderboardRowDesktopLayout
      className="text-text700 text-xs font-semibold uppercase"
      points={customColumn?.point || factionTableHeader.point}
      rank={customColumn?.rank || factionTableHeader.rank}
      title={
        <>
          <span className="3xl:hidden hidden 2xl:block">
            {customColumn?.main?.short || factionTableHeader.main.short}
          </span>
          <span className="3xl:block 2xl:hidden">
            {customColumn?.main?.long || factionTableHeader.main.long}
          </span>
        </>
      }
    />
  );
};

export const FactionLeaderboardDesktop = ({
  data,
  desktopHeader,
  desktopRowHeader,
  containerClassname,
}: Pick<
  FactionLeaderboardProps,
  'data' | 'desktopHeader' | 'desktopRowHeader' | 'containerClassname'
>) => {
  return (
    <LeaderboardDesktop
      className={containerClassname}
      header={desktopHeader}
      rows={
        <>
          {desktopRowHeader || <FactionRowHeaderDesktop />}
          {data.map((item, index) => (
            <div
              className="even:bg-back400 rounded-xl"
              data-rank={item.rank}
              key={index}
            >
              <LeaderboardRowDesktop
                data={item}
                title={<FactionNameAsTitle {...item} />}
                subtitle={item.endDate}
                compact
              />
            </div>
          ))}
        </>
      }
    />
  );
};
